import React, { ReactElement } from 'react';
import Button, { ButtonProps } from '@material-ui/core/Button';
import Box from '@material-ui/core/Box';
import useStyles from './styles';
import TranslatedText from '../../../components/atoms/translated_text';
import { Translations } from './configuration';
import {
  AppDatePicker,
  AppTextField,
} from '../../../components/molecules/form_controls';
import { useForm } from 'react-hook-form';
import _ from 'lodash';
import ConfirmationNumberOutlinedIcon from '@material-ui/icons/ConfirmationNumberOutlined';
import PeopleAltOutlinedIcon from '@material-ui/icons/PeopleAltOutlined';
import EmailOutlinedIcon from '@material-ui/icons/EmailOutlined';
import LockOutlinedIcon from '@material-ui/icons/LockOutlined';
import * as RegisterSelectors from '../../../redux/reducers/register_reducer/selectors';
import { useDispatch } from 'react-redux';
import { StateStatus } from '../../../redux/utils/common';
import { LinearProgress } from '@material-ui/core';
import GridLayout from 'components/molecules/grid_layout';
import TodayIcon from '@material-ui/icons/Today';

// add types or interface
interface FormTypes {
  certicifateDate: string | null;
  vatNumber: string | null;
  username: string | null;
  email: string | null;
  confirmEmail: string | null;
  password: string | null;
  confirmPassword: string | null;
  countryOfBusinessId: string | null;
}

const defaultValues = {
  certicifateDate: null,
  vatNumber: null,
  username: null,
  email: null,
  confirmEmail: null,
  password: null,
  confirmPassword: null,
  countries: null,
};

const emailPattern =
  /^[-_a-zA-Z0-9][-._a-zA-Z0-9]*@[-._a-zA-Z0-9]+(.[-.a-zA-Z0-9]+)*.(COM|EDU|INFO|GOV|INT|MIL|NET|ORG|BIZ|NAME|MUSEUM|COOP|AERO|PRO|TV|com|edu|info|gov|int|mil|net|org|biz|name|museum|coop|aero|pro|tv|[a-zA-Z]{2})$/;
// /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i;

interface IProps {
  onSubmit: (data: any) => any;
}

export default function EUForm(props: IProps): ReactElement {
  // console.log('Render EUForm:');

  const classes = useStyles();
  const { getValues, watch, errors, control, handleSubmit } =
    useForm<FormTypes>({
      defaultValues,
    });
  const dispatch = useDispatch();
  const registerStatus = RegisterSelectors.useGetRegisterStatus();

  const watchEmail = watch('email');
  const watchPassword = watch('password');
  const watchConfirmPassword = watch('confirmPassword');
  const watchEmailConfirm = watch('confirmEmail');

  return (
    <form
      className={classes.form}
      onSubmit={handleSubmit(props.onSubmit)}
      noValidate
    >
      <GridLayout
        elements={[
          {
            element: (
              <AppTextField
                InputLabelProps={{
                  classes: {
                    root: classes.inputLabel,
                    focused: classes.inputLabelFocus,
                  },
                }}
                InputProps={{
                  classes: {
                    focused: classes.notchedOutlineFocus,
                    notchedOutline: classes.notchedOutline,
                  } as any,
                  className: classes.input,
                }}
                variant="outlined"
                // margin="normal"
                required
                fullWidth
                id="vatNumber"
                label="VAT Number"
                error={'vatNumber' in errors}
                helperText={errors.vatNumber ? errors.vatNumber.message : ' '}
                rules={{
                  required: 'Required Field',
                }}
                name="vatNumber"
                autoComplete="vatNumber"
                control={control}
                icon={{
                  position: 'left',
                  Component: (
                    <ConfirmationNumberOutlinedIcon
                      className={classes.inputIcon}
                    />
                  ),
                }}
                disabled={registerStatus === StateStatus.Pending}
              />
            ),
            size: 6,
            id: 'vatNumber',
          },
          {
            element: (
              <AppDatePicker
                InputAdornmentProps={{
                  position: 'start',
                }}
                keyboardIcon={<TodayIcon className={classes.inputIcon} />}
                InputLabelProps={{
                  classes: {
                    root: classes.inputLabel,
                    focused: classes.inputLabelFocus,
                  },
                }}
                InputProps={{
                  classes: {
                    focused: classes.notchedOutlineFocus,
                    notchedOutline: classes.notchedOutline,
                  },
                  className: classes.input,
                }}
                fullWidth
                label="VAT Commencement Date"
                autoOk
                helperText={
                  errors.certicifateDate ? errors.certicifateDate.message : ' '
                }
                error={'certicifateDate' in errors}
                control={control}
                rules={{
                  required: 'Required Field',
                }}
                name="certicifateDate"
                maxDate={new Date()}
                disabled={registerStatus === StateStatus.Pending}
              />
            ),
            size: 6,
            id: 'certicifateDate',
          },
          {
            element: (
              <AppTextField
                InputLabelProps={{
                  classes: {
                    root: classes.inputLabel,
                    focused: classes.inputLabelFocus,
                  },
                }}
                InputProps={{
                  classes: {
                    focused: classes.notchedOutlineFocus,
                    notchedOutline: classes.notchedOutline,
                  } as any,
                  className: classes.input,
                }}
                variant="outlined"
                // margin="normal"
                fullWidth
                id="username"
                label="Username"
                name="username"
                required
                error={'username' in errors}
                autoComplete="username"
                control={control}
                helperText={errors.username ? errors.username.message : ' '}
                rules={{
                  required: 'Required Field',
                }}
                icon={{
                  position: 'left',
                  Component: (
                    <PeopleAltOutlinedIcon className={classes.inputIcon} />
                  ),
                }}
                disabled={registerStatus === StateStatus.Pending}
              />
            ),
            size: 12,
            id: 'username',
          },
          {
            element: (
              <AppTextField
                InputLabelProps={{
                  classes: {
                    root: classes.inputLabel,
                    focused: classes.inputLabelFocus,
                  },
                }}
                InputProps={{
                  classes: {
                    focused: classes.notchedOutlineFocus,
                    notchedOutline: classes.notchedOutline,
                  } as any,
                  className: classes.input,
                }}
                disabled={registerStatus === StateStatus.Pending}
                variant="outlined"
                // margin="normal"
                required
                fullWidth
                control={control}
                error={
                  'password' in errors ||
                  (watchPassword ? watchPassword.length < 8 : false)
                }
                rules={{
                  required: 'Required Field',
                  minLength: {
                    value: 8,
                    message: Translations.password_length_error.English,
                  },
                }}
                helperText={
                  (errors.password &&
                    errors.password.type === 'required' &&
                    errors.password.message) ||
                  (watchPassword &&
                    watchPassword.length < 8 &&
                    Translations.password_length_error.English) ||
                  ' '
                }
                name="password"
                label="Password"
                type="password"
                id="password"
                icon={{
                  position: 'left',
                  Component: <LockOutlinedIcon className={classes.inputIcon} />,
                }}
              />
            ),
            size: 6,
            id: 'password',
          },
          {
            element: (
              <AppTextField
                InputLabelProps={{
                  classes: {
                    root: classes.inputLabel,
                    focused: classes.inputLabelFocus,
                  },
                }}
                InputProps={{
                  classes: {
                    focused: classes.notchedOutlineFocus,
                    notchedOutline: classes.notchedOutline,
                  } as any,
                  className: classes.input,
                }}
                disabled={registerStatus === StateStatus.Pending}
                variant="outlined"
                // margin="normal"
                required
                fullWidth
                name="confirmPassword"
                error={
                  'confirmPassword' in errors ||
                  (watchPassword && watchConfirmPassword
                    ? watchPassword !== watchConfirmPassword
                    : false)
                }
                label="Confirm Password"
                helperText={
                  (errors.confirmPassword &&
                    errors.confirmPassword.type === 'required' &&
                    errors.confirmPassword.message) ||
                  (watchPassword &&
                    watchConfirmPassword &&
                    watchPassword !== watchConfirmPassword &&
                    Translations.password_match_error.English) ||
                  ' '
                }
                type="password"
                id="confirmPassword"
                control={control}
                rules={{
                  required: 'Required Field',
                  validate: () =>
                    getValues('password') === getValues('confirmPassword') ||
                    Translations.password_match_error.English,
                }}
                icon={{
                  position: 'left',
                  Component: <LockOutlinedIcon className={classes.inputIcon} />,
                }}
              />
            ),
            size: 6,
            id: 'confirmPassword',
          },
          {
            element: (
              <AppTextField
                InputLabelProps={{
                  classes: {
                    root: classes.inputLabel,
                    focused: classes.inputLabelFocus,
                  },
                }}
                InputProps={{
                  classes: {
                    focused: classes.notchedOutlineFocus,
                    notchedOutline: classes.notchedOutline,
                  } as any,
                  className: classes.input,
                }}
                disabled={registerStatus === StateStatus.Pending}
                variant="outlined"
                // margin="normal"
                required
                fullWidth
                id="email"
                label="Email Address"
                name="email"
                autoComplete="email"
                control={control}
                error={
                  'email' in errors ||
                  (watchEmail ? !watchEmail?.match(emailPattern) : false)
                }
                helperText={
                  errors.email && errors.email.type === 'required'
                    ? errors.email.message
                    : watchEmail && !watchEmail?.match(emailPattern)
                    ? Translations.invalid_email.English
                    : ' '
                }
                rules={{
                  required: 'Required Field',
                  pattern: {
                    value: emailPattern,
                    message: Translations.invalid_email.English,
                  },
                }}
                icon={{
                  position: 'left',
                  Component: (
                    <EmailOutlinedIcon className={classes.inputIcon} />
                  ),
                }}
              />
            ),
            size: 6,
            id: 'email',
          },
          {
            element: (
              <AppTextField
                InputLabelProps={{
                  classes: {
                    root: classes.inputLabel,
                    focused: classes.inputLabelFocus,
                  },
                }}
                InputProps={{
                  classes: {
                    focused: classes.notchedOutlineFocus,
                    notchedOutline: classes.notchedOutline,
                  } as any,
                  className: classes.input,
                }}
                disabled={registerStatus === StateStatus.Pending}
                variant="outlined"
                // margin="normal"
                required
                fullWidth
                id="confirmEmail"
                label="Confirm Email Address"
                name="confirmEmail"
                autoComplete="confirmEmail"
                control={control}
                error={
                  'confirmEmail' in errors ||
                  (watchEmailConfirm
                    ? !watchEmailConfirm?.match(emailPattern)
                    : false) ||
                  (watchEmail && watchEmailConfirm
                    ? watchEmail !== watchEmailConfirm
                    : false)
                }
                helperText={
                  errors.email && errors.email.type === 'required'
                    ? errors.email.message
                    : (watchEmail &&
                        ((!watchEmailConfirm?.match(emailPattern) &&
                          Translations.invalid_email.English) ||
                          (watchEmail != watchEmailConfirm &&
                            Translations.email_match_error.English))) ||
                      ' '
                }
                rules={{
                  required: 'Required Field',
                  validate: () =>
                    getValues('email') === getValues('confirmEmail') ||
                    Translations.email_match_error.English,
                }}
                icon={{
                  position: 'left',
                  Component: (
                    <EmailOutlinedIcon className={classes.inputIcon} />
                  ),
                }}
              />
            ),
            size: 6,
            id: 'confirmEmail',
          },
        ]}
      />

      {registerStatus === StateStatus.Pending && <LinearProgress />}
      <Box display={'flex'} alignItems={'center'} justifyContent={'center'}>
        <SubmitWrapper
          type="submit"
          fullWidth
          variant="contained"
          color="primary"
          className={classes.submit}
          rstatus={registerStatus}
        >
          <TranslatedText textMap={Translations.form_button_register} />
        </SubmitWrapper>
      </Box>
    </form>
  );
}

interface IButtonProps extends ButtonProps {
  rstatus: StateStatus;
}

const SubmitWrapper = (props: IButtonProps) => {
  return <Button {...props} disabled={props.rstatus === StateStatus.Pending} />;
};
